import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LinkOutlined from "@mui/icons-material/LinkOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import agent from "../../utils/axios";
import { useQuickBooksAuthPopup } from "../../hooks/useQuickBooksAuthPopup";
import {
  Callout,
  PageHeader,
  StatusChip,
  connectionMeta,
  useToast,
} from "../../components/ui";
import { QuickBooksCompany } from "./types";

/**
 * Admin screen to authorize each configured QuickBooks company once, so CSIPay holds its
 * own access token per RealmId. After a company is connected, Convert to Check routes bills
 * to it using the stored token (no per-convert popup). CSIPay owns these tokens — it never
 * borrows COMPASS's.
 */
function ConnectQuickBooksCompanies() {
  const toast = useToast();
  const { authenticateWithReason, inProgress } = useQuickBooksAuthPopup();
  const [companies, setCompanies] = useState<QuickBooksCompany[]>([]);
  const [loading, setLoading] = useState(false);
  const [connectingRealmId, setConnectingRealmId] = useState<string | null>(
    null
  );
  // A load failure is an error; a connect that did not finish is a warning, because the
  // shared popup hook cannot say whether the person closed the window or the sign-in failed.
  const [error, setError] = useState<{
    tone: "err" | "warn";
    text: string;
  } | null>(null);

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result: QuickBooksCompany[] = await agent.quickbooks.getCompanies();
      setCompanies(result);
    } catch (e) {
      setError({ tone: "err", text: "Unable to load QuickBooks companies." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  // Open the QuickBooks sign-in popup for a specific company (RealmId); the shared hook
  // exchanges the code for a token stored under the company the admin chose.
  const connectCompany = async (company: QuickBooksCompany) => {
    setError(null);
    setConnectingRealmId(company.realmId);
    try {
      const result = await authenticateWithReason(company.realmId);
      if (result.ok) {
        toast.success(`${company.name} is connected to QuickBooks.`);
        await loadCompanies();
      } else if (result.reason === "start-failed") {
        setError({ tone: "err", text: "Unable to start QuickBooks sign-in." });
      } else if (result.reason === "auth-failed") {
        setError({
          tone: "err",
          text: "Failed to connect the QuickBooks company. Please try again.",
        });
      } else if (result.reason === "blocked") {
        setError({
          tone: "warn",
          text: "Your browser blocked the QuickBooks sign-in window. Allow pop-ups for this site, then try again.",
        });
      }
      // "cancelled": the person closed the sign-in window; nothing to report.
    } finally {
      setConnectingRealmId(null);
    }
  };

  const busy = connectingRealmId !== null || inProgress;

  return (
    <React.Fragment>
      <PageHeader
        title="Connect QuickBooks"
        helmetTitle="Connect QuickBooks"
        breadcrumbs={[{ label: "Admin" }, { label: "Connect QuickBooks" }]}
        subtitle="Authorize each QuickBooks company once. CSI Pay stores its own access token for that company, so Convert to check can send bills to the correct company without a sign-in popup each time."
      />

      {error && (
        <Callout
          tone={error.tone}
          role="alert"
          sx={{ mb: 4 }}
          action={
            <IconButton
              aria-label="Dismiss"
              color="inherit"
              size="small"
              onClick={() => setError(null)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {error.text}
        </Callout>
      )}

      <Card sx={{ maxWidth: 760 }}>
        <CardHeader
          avatar={<LinkOutlined sx={{ color: "text.secondary" }} />}
          title="QuickBooks connections"
          subheader="When connecting, sign in to the QuickBooks company that matches the row you clicked."
          subheaderTypographyProps={{ variant: "body2" }}
          action={
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={loadCompanies}
              disabled={loading}
            >
              Refresh
            </Button>
          }
        />
        {loading ? (
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading companies…</Typography>
            </Box>
          </CardContent>
        ) : companies.length === 0 ? (
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              No QuickBooks companies are configured for this environment.
            </Typography>
          </CardContent>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Company</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companies.map((company, index) => (
                <TableRow key={`${company.realmId}-${index}`}>
                  <TableCell>
                    <Typography variant="subtitle2">{company.name}</Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      RealmId: {company.realmId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {/* hasValidToken == a non-expired refresh token exists, so the company is
                        usable (the convert flow auto-refreshes an expired access token).
                        Everything else needs sign-in. */}
                    <StatusChip meta={connectionMeta(company.hasValidToken)} />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      variant={company.hasValidToken ? "text" : "outlined"}
                      size="small"
                      disabled={busy}
                      onClick={() => connectCompany(company)}
                      startIcon={
                        connectingRealmId === company.realmId ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : undefined
                      }
                    >
                      {company.hasValidToken ? "Reconnect" : "Connect"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </React.Fragment>
  );
}

export default ConnectQuickBooksCompanies;
